import { createPath, dedupePaths, flattenPaths } from './pressHMI.schema';

// ─── Channel state enum values ────────────────────────────────────────────────
const CHANNEL_STATE = Object.freeze({
  IDLE: 0,
  PURGE_SYRINGE: 10,
  FORMULATE: 20,
  DISPENSING: 30,
  SWITCH_TO_PRESSURE: 35,
  WAIT: 40,
  RETRACTING: 50,
  PRESSURE_ZERO: 60,
  COMPLETE: 100,
  ERROR: 200,
});

const CHANNEL_STATE_LABELS = {
  [CHANNEL_STATE.IDLE]: 'Idle',
  [CHANNEL_STATE.PURGE_SYRINGE]: 'Purging syringe…',
  [CHANNEL_STATE.FORMULATE]: 'Formulating…',
  [CHANNEL_STATE.DISPENSING]: 'Dispensing…',
  [CHANNEL_STATE.SWITCH_TO_PRESSURE]: 'Switching to pressure mode…',
  [CHANNEL_STATE.WAIT]: 'Waiting…',
  [CHANNEL_STATE.RETRACTING]: 'Retracting…',
  [CHANNEL_STATE.PRESSURE_ZERO]: 'Zeroing pressure…',
  [CHANNEL_STATE.COMPLETE]: 'Complete',
  [CHANNEL_STATE.ERROR]: 'Error',
};

const SETUP_TYPE = Object.freeze({ CONTINUOUS_FLOW: 1, DISCRETE_FLOW: 2 });
const SUB_MODE = Object.freeze({ DISPLACEMENT: 1, PRESSURE: 2 });

const TEST_STAND_LIMITS = Object.freeze({
  maxTipVolumeUl: 1000,
  maxPumpSpeedMlSec: 5,
  maxPressureBar: 2,
});

// ─── PLC path helpers ─────────────────────────────────────────────────────────
const runPaths = {
  testName: createPath('GVL_TestStand.sTestName', 'STRING', { size: 80 }),
  testLogId: createPath('GVL_TestStand.sTestLogId', 'STRING', { size: 80 }),
  setupType: createPath('GVL_TestStand.eSetupType', 'INT'),
  subMode: createPath('GVL_TestStand.eSubMode', 'INT'),
  startBoth: createPath('GVL_TestStand.bStartBoth', 'BOOL'),
  stopBoth: createPath('GVL_TestStand.bStopBoth', 'BOOL'),
  abortBoth: createPath('GVL_TestStand.bAbortBoth', 'BOOL'),
  continueBoth: createPath('GVL_TestStand.bContinueBoth', 'BOOL'),
};

const channelPaths = (prefix) => ({
  cmd: {
    startRun: createPath(`${prefix}.Cmd.bStartRun`, 'BOOL'),
    stopRun: createPath(`${prefix}.Cmd.bStopRun`, 'BOOL'),
    abortRun: createPath(`${prefix}.Cmd.bAbortRun`, 'BOOL'),
    continueRun: createPath(`${prefix}.Cmd.bContinue`, 'BOOL'),
    params: {
      // shared across setup types
      targetPressure: createPath(`${prefix}.Cmd.stParams.fTargetPressure`, 'LREAL'),
      dispenseTime: createPath(`${prefix}.Cmd.stParams.fDispenseTime`, 'LREAL'),
      usePressureFeedbackCorrection: createPath(`${prefix}.Cmd.stParams.bUsePressureFeedbackCorrection`, 'BOOL'),
      // displacement
      sampleVolume: createPath(`${prefix}.Cmd.stParams.fSampleVolume`, 'LREAL'),
      airSlugVolume: createPath(`${prefix}.Cmd.stParams.fAirSlugVolume`, 'LREAL'),
      aspirateSpeed: createPath(`${prefix}.Cmd.stParams.fAspirateSpeed`, 'LREAL'),
      dispenseVolume: createPath(`${prefix}.Cmd.stParams.fDispenseVolume`, 'LREAL'),
      dispenseSpeed: createPath(`${prefix}.Cmd.stParams.fDispenseSpeed`, 'LREAL'),
      retractVolume: createPath(`${prefix}.Cmd.stParams.fRetractVolume`, 'LREAL'),
      holdTimeAfterDispense: createPath(`${prefix}.Cmd.stParams.fHoldTimeAfterDispense`, 'LREAL'),
    },
  },
  outputs: {
    vesselPressure: createPath(`${prefix}.Outputs.fVesselPressure`, 'LREAL'),
    pipetteTipPressure: createPath(`${prefix}.Outputs.fPipetteTipPressure`, 'LREAL'),
  },
  status: {
    state: createPath(`${prefix}.Status.eState`, 'INT'),
    currentStep: createPath(`${prefix}.Status.sCurrentStep`, 'STRING', { size: 80 }),
    stepIndex: createPath(`${prefix}.Status.nStepIndex`, 'INT'),
    stepElapsedMs: createPath(`${prefix}.Status.nStepElapsedMs`, 'DWORD'),
    stepRemainingMs: createPath(`${prefix}.Status.nStepRemainingMs`, 'DWORD'),
    errorCode: createPath(`${prefix}.Status.nErrorCode`, 'INT'),
    runActive: createPath(`${prefix}.Status.bRunActive`, 'BOOL'),
    awaitingUser: createPath(`${prefix}.Status.bAwaitingUser`, 'BOOL'),
  },
  actuators: {
    pressureEnable: createPath(`${prefix}.Actuators.bPressureEnable`, 'BOOL'),
    pressureSetpoint: createPath(`${prefix}.Actuators.fPressureSetpoint`, 'LREAL'),
    pneumaticValvePosition: createPath(`${prefix}.Actuators.sPneumaticValvePosition`, 'STRING', { size: 32 }),
    pump: {
      address: createPath(`${prefix}.Actuators.Pump.sAddress`, 'STRING', { size: 1 }),
      request: createPath(`${prefix}.Actuators.Pump.bRequest`, 'BOOL'),
      sequence: createPath(`${prefix}.Actuators.Pump.nSequence`, 'DWORD'),
      commandBody: createPath(`${prefix}.Actuators.Pump.sCommandBody`, 'STRING', { size: 120 }),
      commandLabel: createPath(`${prefix}.Actuators.Pump.sCommandLabel`, 'STRING', { size: 80 }),
      txCommand: createPath(`${prefix}.Actuators.Pump.sTxCommand`, 'STRING', { size: 128 }),
      lastResponse: createPath(`${prefix}.Actuators.Pump.sLastResponse`, 'STRING', { size: 128 }),
      valvePosition: createPath(`${prefix}.Actuators.Pump.sValvePosition`, 'STRING', { size: 32 }),
      busy: createPath(`${prefix}.Actuators.Pump.bBusy`, 'BOOL'),
      done: createPath(`${prefix}.Actuators.Pump.bDone`, 'BOOL'),
      error: createPath(`${prefix}.Actuators.Pump.bError`, 'BOOL'),
      errorCode: createPath(`${prefix}.Actuators.Pump.nErrorCode`, 'INT'),
    },
  },
  logger: {
    fillLevel: createPath(`${prefix}.Logger.nFillLevel`, 'UINT'),
  },
});

const TS_PATHS = {
  run: runPaths,
  lipid: channelPaths('GVL_TestStand.Lipid'),
  aqueous: channelPaths('GVL_TestStand.Aqueous'),
};

const TEST_STAND_MODE = Object.freeze({
  CONTINUOUS_FLOW: 'continuousFlow',
  DISCRETE_DISPLACEMENT: 'discreteDisplacement',
  DISCRETE_PRESSURE: 'discretePressure',
});

const TEST_STAND_MODE_CONFIG = {
  [TEST_STAND_MODE.CONTINUOUS_FLOW]: {
    group: 'continuousFlow',
    paramKeys: ['targetPressure', 'dispenseTime', 'usePressureFeedbackCorrection'],
    fields: [
      ['Target liquid pressure (bar)', 'targetPressure'],
      ['Dispense time (s)', 'dispenseTime'],
      ['Use pressure feedback correction', 'usePressureFeedbackCorrection'],
    ],
  },
  [TEST_STAND_MODE.DISCRETE_DISPLACEMENT]: {
    group: 'discreteDisplacement',
    paramKeys: [
      'sampleVolume',
      'airSlugVolume',
      'aspirateSpeed',
      'dispenseVolume',
      'dispenseSpeed',
      'holdTimeAfterDispense',
      'retractVolume',
    ],
    fields: [
      ['Sample volume (uL)', 'sampleVolume'],
      ['Air slug volume (uL)', 'airSlugVolume'],
      ['Aspirate speed (ml/s)', 'aspirateSpeed'],
      ['Dispense volume (uL)', 'dispenseVolume'],
      ['Dispense speed (ml/s)', 'dispenseSpeed'],
      ['Hold after dispense (s)', 'holdTimeAfterDispense'],
      ['Retract volume (uL)', 'retractVolume'],
    ],
  },
  [TEST_STAND_MODE.DISCRETE_PRESSURE]: {
    group: 'discretePressure',
    paramKeys: [
      'sampleVolume',
      'airSlugVolume',
      'aspirateSpeed',
      'targetPressure',
      'dispenseTime',
      'usePressureFeedbackCorrection',
    ],
    fields: [
      ['Sample volume (uL)', 'sampleVolume'],
      ['Air slug volume (uL)', 'airSlugVolume'],
      ['Aspirate speed (ml/s)', 'aspirateSpeed'],
      ['Target liquid pressure (bar)', 'targetPressure'],
      ['Dispense time (s)', 'dispenseTime'],
      ['Use pressure feedback correction', 'usePressureFeedbackCorrection'],
    ],
  },
};

const FIELD_LIMITS = {
  targetPressure: { min: 0, max: TEST_STAND_LIMITS.maxPressureBar },
  dispenseTime: { min: 0 },
  sampleVolume: { min: 0, max: TEST_STAND_LIMITS.maxTipVolumeUl },
  airSlugVolume: { min: 0, max: TEST_STAND_LIMITS.maxTipVolumeUl },
  aspirateSpeed: { min: 0, max: TEST_STAND_LIMITS.maxPumpSpeedMlSec },
  dispenseVolume: { min: 0, max: TEST_STAND_LIMITS.maxTipVolumeUl },
  dispenseSpeed: { min: 0, max: TEST_STAND_LIMITS.maxPumpSpeedMlSec },
  holdTimeAfterDispense: { min: 0 },
  retractVolume: { min: 0, max: TEST_STAND_LIMITS.maxTipVolumeUl },
};

const numberValue = (value) => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : Number.NaN;
};

const requiredPositive = (value, label) => (
  value > 0 ? '' : `${label} must be greater than 0.`
);

function validateRange(value, label, limits) {
  const fieldLimits = limits || {};
  const n = numberValue(value);
  if (Number.isNaN(n)) return `${label} must be a number.`;
  if (fieldLimits.min !== undefined && n < fieldLimits.min) return `${label} must be at least ${fieldLimits.min}.`;
  if (fieldLimits.max !== undefined && n > fieldLimits.max) return `${label} must be ${fieldLimits.max} or less.`;
  return '';
}

function validateChannelParams(params, setupType, subMode) {
  const channelParams = params || {};
  if (setupType === SETUP_TYPE.CONTINUOUS_FLOW) {
    return validateRange(channelParams.targetPressure, 'Target pressure', FIELD_LIMITS.targetPressure)
      || requiredPositive(numberValue(channelParams.dispenseTime), 'Dispense time');
  }

  if (setupType !== SETUP_TYPE.DISCRETE_FLOW) return '';

  const sampleVolume = numberValue(channelParams.sampleVolume);
  const airSlugVolume = numberValue(channelParams.airSlugVolume);
  const aspirateSpeed = numberValue(channelParams.aspirateSpeed);
  const targetPressure = numberValue(channelParams.targetPressure);
  const dispenseTime = numberValue(channelParams.dispenseTime);
  const totalAspirateVolume = sampleVolume + airSlugVolume;

  const aspirateError = validateRange(sampleVolume, 'Sample volume', FIELD_LIMITS.sampleVolume)
    || validateRange(airSlugVolume, 'Air slug volume', FIELD_LIMITS.airSlugVolume)
    || validateRange(aspirateSpeed, 'Aspirate speed', FIELD_LIMITS.aspirateSpeed)
    || (totalAspirateVolume > TEST_STAND_LIMITS.maxTipVolumeUl
      ? `Air slug plus sample volume must be ${TEST_STAND_LIMITS.maxTipVolumeUl} uL or less. Current total: ${totalAspirateVolume} uL.`
      : '')
    || (totalAspirateVolume > 0
      ? requiredPositive(aspirateSpeed, 'Aspirate speed')
      : '');

  if (subMode === SUB_MODE.PRESSURE) {
    return aspirateError
      || validateRange(targetPressure, 'Target pressure', FIELD_LIMITS.targetPressure)
      || requiredPositive(dispenseTime, 'Dispense time');
  }

  const dispenseVolume = numberValue(channelParams.dispenseVolume);
  const dispenseSpeed = numberValue(channelParams.dispenseSpeed);
  const retractVolume = numberValue(channelParams.retractVolume);
  const holdTimeAfterDispense = numberValue(channelParams.holdTimeAfterDispense);

  return aspirateError
    || validateRange(dispenseVolume, 'Dispense volume', FIELD_LIMITS.dispenseVolume)
    || validateRange(dispenseSpeed, 'Dispense speed', FIELD_LIMITS.dispenseSpeed)
    || validateRange(retractVolume, 'Retract volume', FIELD_LIMITS.retractVolume)
    || validateRange(holdTimeAfterDispense, 'Hold after dispense', FIELD_LIMITS.holdTimeAfterDispense)
    || ((dispenseVolume > 0 || retractVolume > 0)
      ? requiredPositive(dispenseSpeed, 'Dispense speed')
      : '');
}

function getFieldLimits(key) {
  return FIELD_LIMITS[key] || {};
}

const getTestStandMode = (setupType, subMode) => {
  if (setupType === SETUP_TYPE.DISCRETE_FLOW) {
    return subMode === SUB_MODE.PRESSURE
      ? TEST_STAND_MODE.DISCRETE_PRESSURE
      : TEST_STAND_MODE.DISCRETE_DISPLACEMENT;
  }

  return TEST_STAND_MODE.CONTINUOUS_FLOW;
};

const getTestStandModeConfig = (setupType, subMode) => (
  TEST_STAND_MODE_CONFIG[getTestStandMode(setupType, subMode)]
);

const getChannelParamHandles = (paths, paramKeys) => (
  paramKeys.map((key) => paths.cmd.params[key])
);

// ─── Groups ───────────────────────────────────────────────────────────────────
const TEST_STAND_GROUPS = {
  base: dedupePaths([
    ...flattenPaths(TS_PATHS.run),
    ...flattenPaths(TS_PATHS.lipid.status),
    ...flattenPaths(TS_PATHS.aqueous.status),
    ...flattenPaths(TS_PATHS.lipid.outputs),
    ...flattenPaths(TS_PATHS.aqueous.outputs),
    TS_PATHS.lipid.actuators.pressureEnable,
    TS_PATHS.lipid.actuators.pressureSetpoint,
    TS_PATHS.aqueous.actuators.pressureEnable,
    TS_PATHS.aqueous.actuators.pressureSetpoint,
    TS_PATHS.lipid.logger.fillLevel,
    TS_PATHS.aqueous.logger.fillLevel,
  ]),
  continuousFlow: dedupePaths([
    ...flattenPaths(TS_PATHS.lipid.cmd).filter((path) => !path.name.includes('.stParams.')),
    ...flattenPaths(TS_PATHS.aqueous.cmd).filter((path) => !path.name.includes('.stParams.')),
    ...getChannelParamHandles(
      TS_PATHS.lipid,
      TEST_STAND_MODE_CONFIG[TEST_STAND_MODE.CONTINUOUS_FLOW].paramKeys,
    ),
    ...getChannelParamHandles(
      TS_PATHS.aqueous,
      TEST_STAND_MODE_CONFIG[TEST_STAND_MODE.CONTINUOUS_FLOW].paramKeys,
    ),
  ]),
  discreteDisplacement: dedupePaths([
    ...flattenPaths(TS_PATHS.lipid.cmd).filter((path) => !path.name.includes('.stParams.')),
    ...flattenPaths(TS_PATHS.aqueous.cmd).filter((path) => !path.name.includes('.stParams.')),
    ...flattenPaths(TS_PATHS.lipid.actuators.pump),
    ...flattenPaths(TS_PATHS.aqueous.actuators.pump),
    TS_PATHS.lipid.actuators.pneumaticValvePosition,
    TS_PATHS.aqueous.actuators.pneumaticValvePosition,
    ...getChannelParamHandles(
      TS_PATHS.lipid,
      TEST_STAND_MODE_CONFIG[TEST_STAND_MODE.DISCRETE_DISPLACEMENT].paramKeys,
    ),
    ...getChannelParamHandles(
      TS_PATHS.aqueous,
      TEST_STAND_MODE_CONFIG[TEST_STAND_MODE.DISCRETE_DISPLACEMENT].paramKeys,
    ),
  ]),
  discretePressure: dedupePaths([
    ...flattenPaths(TS_PATHS.lipid.cmd).filter((path) => !path.name.includes('.stParams.')),
    ...flattenPaths(TS_PATHS.aqueous.cmd).filter((path) => !path.name.includes('.stParams.')),
    ...flattenPaths(TS_PATHS.lipid.actuators.pump),
    ...flattenPaths(TS_PATHS.aqueous.actuators.pump),
    TS_PATHS.lipid.actuators.pneumaticValvePosition,
    TS_PATHS.aqueous.actuators.pneumaticValvePosition,
    ...getChannelParamHandles(
      TS_PATHS.lipid,
      TEST_STAND_MODE_CONFIG[TEST_STAND_MODE.DISCRETE_PRESSURE].paramKeys,
    ),
    ...getChannelParamHandles(
      TS_PATHS.aqueous,
      TEST_STAND_MODE_CONFIG[TEST_STAND_MODE.DISCRETE_PRESSURE].paramKeys,
    ),
  ]),
};

const getTestStandGroupNames = (setupType, subMode) => ([
  'base',
  getTestStandModeConfig(setupType, subMode).group,
]);

export {
  CHANNEL_STATE,
  CHANNEL_STATE_LABELS,
  getFieldLimits,
  getTestStandGroupNames,
  getTestStandMode,
  getTestStandModeConfig,
  SETUP_TYPE,
  SUB_MODE,
  TEST_STAND_GROUPS,
  TEST_STAND_LIMITS,
  TEST_STAND_MODE,
  TEST_STAND_MODE_CONFIG,
  TS_PATHS,
  validateChannelParams,
};
